// packages/shared/src/map/generate.ts
import { FRUITS, getEntry, tuning, type FruitType, type Rot, type WorldEntry } from '@whiskerton/content';
import type { Rect, TileXY } from '../types/geom';
import { GROUND, type GeneratedTown, type GenObject, type Ground, type MapData, type Plot, type Tier, type WaterArea } from '../types/map';
import type { ObjectState } from '../types/world';
import { rngFrom, type Rng } from '../rng';
import { tileByte } from './codec';
import { buildNavGrid, canStep, DIRS, footprintTiles } from './nav';

const M = tuning.map, T = tuning.town;
const W = M.w, H = M.h;
const G = GROUND;
const WATER = new Set<number>([G.river, G.pond, G.sea]);

/** Thrown inside an attempt when a guarantee can't be met; the caller retries with retry + 1. */
class Retry extends Error {}

export function generateTown(seed: number, opts: { genVersion?: number } = {}): GeneratedTown {
  const genVersion = opts.genVersion ?? M.genVersion;
  for (let retry = 0; retry < M.maxRetries; retry++) {
    try {
      return attempt(seed, genVersion, retry);
    } catch (e) {
      if (!(e instanceof Retry)) throw e;
    }
  }
  throw new Error(`generateTown(${seed}): no valid layout after ${M.maxRetries} attempts`);
}

// ---------- helpers
const idx = (x: number, y: number) => y * W + x;
const inB = (x: number, y: number) => x >= 0 && y >= 0 && x < W && y < H;
const smooth = (e0: number, e1: number, v: number) => { const t = Math.min(1, Math.max(0, (v - e0) / (e1 - e0))); return t * t * (3 - 2 * t); };
function noise(rng: Rng, cell: number): (x: number, y: number) => number {
  const gw = Math.ceil(W / cell) + 2, gh = Math.ceil(H / cell) + 2;
  const g = new Float32Array(gw * gh);
  for (let i = 0; i < g.length; i++) g[i] = rng.next();
  return (x, y) => {
    const fx = x / cell, fy = y / cell, ix = Math.floor(fx), iy = Math.floor(fy);
    const u = smooth(0, 1, fx - ix), v = smooth(0, 1, fy - iy);
    const a = g[iy * gw + ix]!, b = g[iy * gw + ix + 1]!, c = g[(iy + 1) * gw + ix]!, d = g[(iy + 1) * gw + ix + 1]!;
    return a + (b - a) * u + (c - a) * v + (a - b - c + d) * u * v;
  };
}
/** Multi-source BFS (4-neighbour) distance from every tile where `src` is true. */
function distanceFrom(src: (i: number) => boolean, pass: (i: number) => boolean = () => true): Int32Array {
  const d = new Int32Array(W * H).fill(-1), q = new Int32Array(W * H);
  let head = 0, tail = 0;
  for (let i = 0; i < W * H; i++) if (src(i)) { d[i] = 0; q[tail++] = i; }
  while (head < tail) {
    const i = q[head++]!, x = i % W, y = (i - x) / W;
    for (const [dx, dy] of DIRS) {
      const nx = x + dx, ny = y + dy;
      if (!inB(nx, ny)) continue;
      const j = idx(nx, ny);
      if (d[j] !== -1 || !pass(j)) continue;
      d[j] = d[i]! + 1;
      q[tail++] = j;
    }
  }
  return d;
}
/** 4-connected components of tiles where `inSet` holds; returns a label per tile (-1 = not in set) and the sizes. */
function components(inSet: (i: number) => boolean): { label: Int32Array; sizes: number[] } {
  const label = new Int32Array(W * H).fill(-1), sizes: number[] = [], q = new Int32Array(W * H);
  for (let s = 0; s < W * H; s++) {
    if (label[s] !== -1 || !inSet(s)) continue;
    const id = sizes.length;
    let head = 0, tail = 0, n = 0;
    label[s] = id; q[tail++] = s;
    while (head < tail) {
      const i = q[head++]!, x = i % W, y = (i - x) / W;
      n++;
      for (const [dx, dy] of DIRS) {
        const nx = x + dx, ny = y + dy;
        if (!inB(nx, ny)) continue;
        const j = idx(nx, ny);
        if (label[j] === -1 && inSet(j)) { label[j] = id; q[tail++] = j; }
      }
    }
    sizes.push(n);
  }
  return { label, sizes };
}
/** Every tile with a set `mask` tile within Chebyshev distance r (clamped to the map): nearMask for all tiles at once. */
function dilate(mask: Uint8Array, r: number): Uint8Array {
  const row = new Uint8Array(W * H), out = new Uint8Array(W * H), run = new Int32Array(Math.max(W, H) + 1);
  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) run[x + 1] = run[x]! + (mask[idx(x, y)] ? 1 : 0);
    for (let x = 0; x < W; x++) if (run[Math.min(W, x + r + 1)]! - run[Math.max(0, x - r)]! > 0) row[idx(x, y)] = 1;
  }
  for (let x = 0; x < W; x++) {
    for (let y = 0; y < H; y++) run[y + 1] = run[y]! + row[idx(x, y)]!;
    for (let y = 0; y < H; y++) if (run[Math.min(H, y + r + 1)]! - run[Math.max(0, y - r)]! > 0) out[idx(x, y)] = 1;
  }
  return out;
}
/** Summed-area table of the tiles where `ok` holds, and how many of them lie in a rect (which must be inside the map). */
function countTable(ok: (i: number) => boolean): (r: Rect) => number {
  const t = new Int32Array((W + 1) * (H + 1));
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
    t[(y + 1) * (W + 1) + x + 1] = (ok(idx(x, y)) ? 1 : 0) + t[y * (W + 1) + x + 1]! + t[(y + 1) * (W + 1) + x]! - t[y * (W + 1) + x]!;
  }
  return (r) => t[(r.y + r.h) * (W + 1) + r.x + r.w]! - t[r.y * (W + 1) + r.x + r.w]! - t[(r.y + r.h) * (W + 1) + r.x]! + t[r.y * (W + 1) + r.x]!;
}
const rectTiles = (r: Rect) => { const out: number[] = []; for (let y = r.y; y < r.y + r.h; y++) for (let x = r.x; x < r.x + r.w; x++) out.push(idx(x, y)); return out; };
const grow = (r: Rect, m: number): Rect => ({ x: r.x - m, y: r.y - m, w: r.w + 2 * m, h: r.h + 2 * m });
const rectGap = (a: Rect, b: Rect) => Math.max(0, Math.max(a.x - (b.x + b.w), b.x - (a.x + a.w)), Math.max(a.y - (b.y + b.h), b.y - (a.y + a.h)));
const centre = (r: Rect) => ({ x: r.x + r.w / 2, y: r.y + r.h / 2 });
function worldEntry(id: string): WorldEntry {
  const e = getEntry(id);
  if (!e || e.kind !== 'world') throw new Error(`map generator: ${id} is not a world catalog entry`);
  return e;
}

function attempt(seed: number, genVersion: number, retry: number): GeneratedTown {
  const rng = rngFrom('map', seed, genVersion, retry);
  const ground = new Uint8Array(W * H).fill(G.sea);
  const tier = new Uint8Array(W * H);
  const rampDir = new Uint8Array(W * H);
  const isLand = (i: number) => !WATER.has(ground[i]!);

  // 1. Island: a squarish superellipse with a noisy coast; sea on every border tile.
  const n1 = noise(rng, 14), n2 = noise(rng, 6), n3 = noise(rng, 16), n4 = noise(rng, 8), n5 = noise(rng, 10);
  const cx = W / 2 + (rng.next() - 0.5) * 6, cy = H / 2 - 2 + (rng.next() - 0.5) * 4;
  const rx = W / 2 - M.seaBorder - 2, ry = H / 2 - M.seaBorder - 1.5;
  for (let y = M.seaBorder; y < H - M.seaBorder; y++) for (let x = M.seaBorder; x < W - M.seaBorder; x++) {
    const dx = Math.abs(x + 0.5 - cx) / rx, dy = Math.abs(y + 0.5 - cy) / ry;
    const d = Math.pow(dx ** 4 + dy ** 4, 0.25) + (n1(x, y) - 0.5) * 0.22 + (n2(x, y) - 0.5) * 0.08;
    if (d < 1) ground[idx(x, y)] = G.grass;
  }
  // Keep the biggest landmass; fill lakes that don't touch the open sea.
  {
    const { label, sizes } = components(isLand);
    const keep = sizes.indexOf(Math.max(...sizes));
    for (let i = 0; i < W * H; i++) if (label[i] !== -1 && label[i] !== keep) ground[i] = G.sea;
    const open = distanceFrom((i) => { const x = i % W, y = (i - x) / W; return ground[i] === G.sea && (x === 0 || y === 0 || x === W - 1 || y === H - 1); }, (i) => ground[i] === G.sea);
    for (let i = 0; i < W * H; i++) if (ground[i] === G.sea && open[i] === -1) ground[i] = G.grass;
  }
  const distSea = distanceFrom((i) => ground[i] === G.sea);
  const landCount = distSea.reduce((n, d) => n + (d > 0 ? 1 : 0), 0);

  // 2. Tiers: sand shore (wide in the south), main town level, plateaus toward the north.
  // Sand beach along the south; elsewhere the main level meets the sea as a low cliff.
  const southness = (y: number) => smooth(cy - 4, cy + 26, y);
  const beachAt = (x: number, y: number) => Math.round((M.beachDepth - 1) * southness(y) + (n2(x, y) - 0.5) * 2);
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
    const i = idx(x, y);
    if (ground[i] === G.sea) continue;
    if (southness(y) > 0.3 && distSea[i]! <= beachAt(x, y)) { ground[i] = G.sand; tier[i] = 0; } else tier[i] = 1;
  }
  {
    const { label, sizes } = components((i) => ground[i] === G.sand);
    for (let i = 0; i < W * H; i++) if (label[i]! >= 0 && sizes[label[i]!]! < 60) { ground[i] = G.grass; tier[i] = 1; }
  }
  const plateauScore = (x: number, y: number) => n3(x, y) * 0.7 + n4(x, y) * 0.3 + (1 - y / H) * 0.35;
  const plateauOk = (x: number, y: number) => tier[idx(x, y)] === 1 && distSea[idx(x, y)]! >= Math.max(0, beachAt(x, y)) + 5;
  {
    const cand: number[] = [];
    for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) if (plateauOk(x, y)) cand.push(plateauScore(x, y));
    cand.sort((a, b) => b - a);
    const cut = cand[Math.min(cand.length - 1, Math.floor(landCount * 0.2))] ?? Infinity;
    const raised = new Uint8Array(W * H);
    for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) if (plateauOk(x, y) && plateauScore(x, y) >= cut) raised[idx(x, y)] = 1;
    majority(raised, 2);
    for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) if (raised[idx(x, y)] && !plateauOk(x, y)) raised[idx(x, y)] = 0;
    dropSmall(raised, 30);
    for (let i = 0; i < W * H; i++) if (raised[i]) tier[i] = 2;
    // Tier 3: the high cores of tier-2 plateaus, always ringed by tier 2.
    const edge2 = distanceFrom((i) => tier[i] !== 2);
    const top: number[] = [];
    for (let i = 0; i < W * H; i++) if (edge2[i]! >= 3) top.push(plateauScore(i % W, Math.floor(i / W)));
    top.sort((a, b) => b - a);
    const cut3 = top[Math.floor(top.length * 0.35)] ?? Infinity;
    const high = new Uint8Array(W * H);
    for (let i = 0; i < W * H; i++) if (edge2[i]! >= 3 && plateauScore(i % W, Math.floor(i / W)) >= cut3) high[i] = 1;
    majority(high, 1);
    for (let i = 0; i < W * H; i++) if (high[i] && edge2[i]! < 2) high[i] = 0;
    dropSmall(high, 12);
    for (let i = 0; i < W * H; i++) if (high[i]) tier[i] = 3;
  }

  // 3. Rivers: from high ground in the north down to the southern sea; 2 wide, 3 at the mouth. A second river may join the first.
  const toSouthSea = distanceFrom((i) => ground[i] === G.sea && Math.floor(i / W) >= cy + 8, (i) => ground[i] !== G.sea);
  const rivers = rng.int(M.rivers[0], M.rivers[1]);
  const riverMask = new Uint8Array(W * H);
  for (let r = 0; r < rivers; r++) {
    const sources: number[] = [], nearRiver = dilate(riverMask, 18);
    for (let i = 0; i < W * H; i++) {
      if (!isLand(i) || toSouthSea[i]! < (r === 0 ? 45 : 30) || nearRiver[i]) continue;
      if (r === 0 && tier[i]! < 2) continue;
      sources.push(i);
    }
    if (sources.length === 0) { if (r === 0) throw new Retry('no river source'); break; }
    let cur = rng.pick(sources), dir = -1;
    for (let guard = 0; guard < 600; guard++) {
      const x = cur % W, y = (cur - x) / W;
      if (ground[cur] === G.sea) break;
      if (r > 0 && riverMask[cur] === 2) break; // joined the first river
      const across = dir === 1 || dir === 3 ? [0, 1] : [1, 0];   // widen perpendicular to the flow
      paint(x, y, r);
      paint(x + across[0]!, y + across[1]!, r);
      if (distSea[cur]! <= 4) paint(x - across[0]!, y - across[1]!, r);
      const opts: { d: number; i: number }[] = [];
      for (let d = 0; d < 4; d++) {
        const nx = x + DIRS[d]![0], ny = y + DIRS[d]![1];
        if (inB(nx, ny) && toSouthSea[idx(nx, ny)]! < toSouthSea[cur]!) opts.push({ d, i: idx(nx, ny) });
      }
      if (opts.length === 0) throw new Retry('river stuck');
      const keep = opts.find((o) => o.d === dir);
      const next = keep && rng.chance(0.6) ? keep : rng.pick(opts);
      dir = next.d;
      cur = next.i;
    }
  }
  function paint(x: number, y: number, r: number) {
    if (!inB(x, y)) return;
    const i = idx(x, y);
    if (ground[i] === G.sea) return;
    ground[i] = G.river;
    riverMask[i] = r === 0 ? 2 : 1;
  }
  if (!hasRiverMouth()) throw new Retry('no river mouth');

  // 4. Ponds on the main level, clear of rivers, coasts and cliffs.
  const ponds = rng.int(M.ponds[0], M.ponds[1]);
  const edge1 = distanceFrom((i) => tier[i] !== 1 || ground[i] !== G.grass);
  const pondCentres: TileXY[] = [], nearRiver = dilate(riverMask, 5);   // ponds never touch the river mask
  for (let p = 0; p < ponds; p++) {
    const cands: number[] = [];
    for (let i = 0; i < W * H; i++) {
      if (edge1[i]! < 6 || nearRiver[i]) continue;
      const x = i % W, y = (i - x) / W;
      if (pondCentres.some((c) => Math.hypot(c.x - x, c.y - y) < 14)) continue;
      cands.push(i);
    }
    if (cands.length === 0) break;
    const c = rng.pick(cands), px = c % W, py = (c - px) / W, rad = 2 + rng.next() * 1.4;
    for (let y = py - 4; y <= py + 4; y++) for (let x = px - 4; x <= px + 4; x++) {
      if (!inB(x, y)) continue;
      if (Math.hypot(x - px, (y - py) * 1.15) <= rad * (0.85 + n2(x, y) * 0.3)) ground[idx(x, y)] = G.pond;
    }
    pondCentres.push({ x: px, y: py });
  }
  if (pondCentres.length === 0) throw new Retry('no pond');

  // 5. Plaza near the centre; shop and museum (with door tiles) within reach of it.
  // Main-level grass per rect from a summed-area table, rebuilt when the plaza is paved (bestRect asks for every rect on the map).
  let flatCount = countTable((i) => tier[i] === 1 && ground[i] === G.grass);
  const flat = (r: Rect, margin: number) => {
    const g = grow(r, margin);
    if (g.x < 0 || g.y < 0 || g.x + g.w > W || g.y + g.h > H) return false;
    return flatCount(g) === g.w * g.h;
  };
  const taken: Rect[] = [];
  const free = (r: Rect, gap: number) => taken.every((t) => !(t.x - gap < r.x + r.w && r.x < t.x + t.w + gap && t.y - gap < r.y + r.h && r.y < t.y + t.h + gap));
  const plaza = bestRect(M.plazaW, M.plazaH, (r) => flat(r, 3), (r) => Math.hypot(centre(r).x - W / 2, centre(r).y - (H / 2 + 2)) + rng.next() * 4);
  if (!plaza) throw new Retry('no plaza site');
  taken.push(plaza);
  const nearPlaza = (r: Rect) => rectGap(r, plaza) <= M.sitesNearPlaza;
  const site = (w: number, h: number) => {
    const r = bestRect(w, h, (c) => flat(c, 2) && free(c, 3) && nearPlaza(c), (c) => Math.abs(rectGap(c, plaza) - 5) + rng.next() * 6);
    if (!r) throw new Retry('no civic site');
    taken.push(r);
    return r;
  };
  const shop = site(M.shopW, M.shopH);
  const museum = site(M.museumW, M.museumH);
  const doorOf = (r: Rect): TileXY => ({ x: r.x + Math.floor(r.w / 2), y: r.y + r.h });
  const shopDoor = doorOf(shop), museumDoor = doorOf(museum);
  for (const i of rectTiles(plaza)) ground[i] = G.path;
  flatCount = countTable((i) => tier[i] === 1 && ground[i] === G.grass);
  const cafe = bestRect(3, 3, (c) => flat(c, 2) && free(c, 3) && nearPlaza(c), () => rng.next());
  if (cafe) taken.push(cafe);
  const market = bestRect(3, 3, (c) => flat(c, 2) && free(c, 3) && nearPlaza(c), () => rng.next());
  if (market) taken.push(market);

  // 6. Connect every walkable region: ramps between tiers, bridges over rivers.
  const bridges: { x: number; y: number; rot: Rot }[] = [];
  const bridgeMask = new Uint8Array(W * H);
  const spawn: TileXY = { x: plaza.x + Math.floor(plaza.w / 2), y: plaza.y + plaza.h - 2 };
  const reserved = new Uint8Array(W * H);
  for (const r of taken) for (const i of rectTiles(grow(r, 1))) if (inB(i % W, Math.floor(i / W))) reserved[i] = 1;
  const navNow = () => {
    const map = { w: W, h: H, tiles: encode(ground, tier, rampDir) };
    return { map, nav: buildNavGrid(map, bridges.map((b) => ({ ...b, catalogId: 'w.bridge', kind: 'bridge' }))) };
  };
  for (let round = 0; ; round++) {
    const { map, nav } = navNow();
    const reach = flood(nav, map, spawn);
    const lost = components((i) => nav.walk[i] === 1 && !reach[i]);
    if (lost.sizes.length === 0) break;
    if (round > 80) throw new Retry('too many regions');
    const target = lost.sizes.indexOf(Math.max(...lost.sizes));
    const c = connectors(nav, (i) => lost.label[i] === target, (i) => reach[i] === 1);
    if (c.bridges.length) addBridge(rng.pick(c.bridges));
    else if (c.ramps.length) addRamp(rng.pick(c.ramps));
    else throw new Retry('unreachable region');
  }
  // A few extra ramps and bridges so the beach, plateaus and river banks have more than one way in.
  {
    const { map, nav } = navNow();
    const reach = flood(nav, map, spawn);
    const c = connectors(nav, (i) => reach[i] === 1, (i) => reach[i] === 1);
    const ramps: number[] = [];
    for (let i = 0; i < W * H; i++) if (ground[i] === G.ramp) ramps.push(i);
    const far = (i: number, list: number[], d: number) => list.every((k) => Math.max(Math.abs((k % W) - (i % W)), Math.abs(Math.floor(k / W) - Math.floor(i / W))) >= d);
    let extraRamps = 0;
    for (const r of rng.shuffle(c.ramps)) {
      if (extraRamps >= 8) break;
      if (ground[r.a] === G.ramp || ground[r.b] === G.ramp || !far(r.a, ramps, 24)) continue;
      addRamp(r);
      ramps.push(r.a);
      extraRamps++;
    }
    const spans = bridges.map((b) => idx(b.x, b.y));
    let extraBridges = 0;
    for (const b of rng.shuffle(c.bridges)) {
      if (extraBridges >= 2) break;
      const i = idx(b.x, b.y);
      if (!far(i, spans, 20) || footprintTiles(b, [4, 2]).some(([x, y]) => bridgeMask[idx(x, y)] || ground[idx(x, y)] === G.ramp)) continue;
      addBridge(b);
      spans.push(i);
      extraBridges++;
    }
  }
  function addBridge(b: { x: number; y: number; rot: Rot }) {
    bridges.push(b);
    for (const [x, y] of footprintTiles(b, [4, 2])) bridgeMask[idx(x, y)] = 1;
  }
  function addRamp(r: { a: number; b: number; d: number }) {
    for (const k of [r.a, r.b]) { ground[k] = G.ramp; rampDir[k] = r.d; }
  }
  /** Ramp and bridge placements that join a tile in A to a tile in B. */
  function connectors(nav: { walk: Uint8Array }, inA: (i: number) => boolean, inB_: (i: number) => boolean) {
    const ramps: { a: number; b: number; d: number }[] = [];
    const brs: { x: number; y: number; rot: Rot }[] = [];
    const open = (k: number, t: number) => nav.walk[k] === 1 && tier[k] === t && ground[k] !== G.ramp && !reserved[k] && !bridgeMask[k];
    for (let i = 0; i < W * H; i++) {
      if (!inA(i)) continue;
      for (let d = 0; d < 4; d++) {
        const [dx, dy] = DIRS[d]!;
        // Ramp: two lower tiles that climb onto two upper tiles, with room to walk on at the bottom.
        const j = step(i, dx, dy);
        if (j >= 0 && inB_(j) && Math.abs(tier[i]! - tier[j]!) === 1) {
          const [low, up, dir] = tier[i]! < tier[j]! ? [i, j, d] : [j, i, (d + 2) % 4];
          const back = DIRS[(dir + 2) % 4]!, pd = DIRS[(dir + 1) % 4]!;
          for (const side of [1, -1]) {
            const low2 = step(low, pd[0] * side, pd[1] * side), up2 = step(up, pd[0] * side, pd[1] * side);
            const lb = step(low, back[0], back[1]), lb2 = low2 < 0 ? -1 : step(low2, back[0], back[1]);
            if ([low2, up2, lb, lb2].some((k) => k < 0)) continue;
            if ([low, low2, lb, lb2].every((k) => open(k, tier[low]!)) && [up, up2].every((k) => open(k, tier[up]!))) ramps.push({ a: low, b: low2, d: dir });
          }
        }
        // Bridge: bank, two river tiles, bank, on the same tier, two lanes wide.
        const near = step(i, dx, dy);
        if (near < 0 || ground[near] !== G.river) continue;             // most tiles: no river next door (checked before building the line)
        const line = [0, 1, 2, 3].map((k) => step(i, dx * k, dy * k));
        if (line.some((k) => k < 0)) continue;
        const [b0, r1, r2, b3] = line as [number, number, number, number];
        if (ground[r1] !== G.river || ground[r2] !== G.river || !inB_(b3) || !open(b3, tier[b0]!) || !open(b0, tier[b0]!)) continue;
        const pd = DIRS[(d + 1) % 4]!;
        const lane = line.map((k) => step(k, pd[0], pd[1]));
        if (lane.some((k) => k < 0)) continue;
        const [c0, s1, s2, c3] = lane as [number, number, number, number];
        if (ground[s1] !== G.river || ground[s2] !== G.river || !open(c0, tier[b0]!) || !open(c3, tier[b0]!)) continue;
        const all = [...line, ...lane];
        if (all.some((k) => reserved[k])) continue;
        brs.push({ x: Math.min(...all.map((k) => k % W)), y: Math.min(...all.map((k) => Math.floor(k / W))), rot: (dx !== 0 ? 0 : 1) as Rot });
      }
    }
    return { ramps, bridges: brs };
  }

  // 7. Ten plots: flat, clear of water, cliffs, ramps, bridges and the civic sites; spread out across the island.
  const clearance = distanceFrom((i) => tier[i] !== 1 || ground[i] !== G.grass || bridgeMask[i] === 1);
  const clear3 = countTable((i) => clearance[i]! >= 3);
  const plotFits = (r: Rect) => r.x >= 0 && r.y >= 0 && r.x + r.w <= W && r.y + r.h <= H && clear3(r) === r.w * r.h && free(r, M.plotGap);
  const cands: Rect[] = [];
  for (let y = 0; y + T.plotH <= H; y++) for (let x = 0; x + T.plotW <= W; x++) { const r = { x, y, w: T.plotW, h: T.plotH }; if (plotFits(r)) cands.push(r); }
  const plots: Rect[] = [];
  const pc = centre(plaza);
  for (let n = 0; n < T.plots; n++) {
    let best: Rect | null = null, bestScore = -Infinity;
    const others = [pc, centre(shop), centre(museum), ...plots.map(centre)];
    for (const c of cands) {
      if (!free(c, M.plotGap)) continue;
      const cc = centre(c);
      const spread = Math.min(...others.map((o) => Math.hypot(o.x - cc.x, o.y - cc.y)));
      const dPlaza = Math.hypot(pc.x - cc.x, pc.y - cc.y);
      const score = Math.min(spread, 26) - Math.max(0, dPlaza - 42) * 0.6 + rng.next() * 3;
      if (score > bestScore) { bestScore = score; best = c; }
    }
    if (!best) throw new Retry('plots do not fit');
    plots.push(best);
    taken.push(best);
  }
  plots.sort((a, b) => a.y - b.y || a.x - b.x);
  const plotList: Plot[] = plots.map((rect, index) => ({
    index, rect, tier: 1 as Tier,
    house: { x: rect.x + 2, y: rect.y + 1, w: T.houseW, h: T.houseH },
    door: { x: rect.x + 3, y: rect.y + 4 },
    mailbox: { x: rect.x + 5, y: rect.y + 4 },
  }));

  // 8. Civic objects and paths from the plaza to the shop and museum doors.
  const objects: GenObject[] = [];
  const occupied = new Uint8Array(W * H);
  const place = (catalogId: string, x: number, y: number, rot: Rot, state: ObjectState = {}) => {
    const e = worldEntry(catalogId);
    objects.push({ kind: e.object, catalogId, x, y, rot, state });
    for (const [tx, ty] of footprintTiles({ x, y, rot }, e.footprint)) occupied[idx(tx, ty)] = 1;
  };
  const well = { x: plaza.x + plaza.w / 2 - 1, y: plaza.y + Math.floor(plaza.h / 2) - 2 };
  place('w.well', well.x, well.y, 0, { role: 'well' });
  place('w.shop', shop.x, shop.y, 0, { role: 'shop' });
  place('w.museum', museum.x, museum.y, 0, { role: 'museum' });
  if (cafe) place('w.cafe', cafe.x, cafe.y, 0, { role: 'cafe' });
  if (market) place('w.market', market.x, market.y, 0, { role: 'market' });
  place('w.board', plaza.x + 1, plaza.y, 0);
  const dropAnchor = { x: shop.x + shop.w - 1, y: shop.y + shop.h };
  place('w.dropbox', dropAnchor.x, dropAnchor.y, 0);
  for (const b of bridges) place('w.bridge', b.x, b.y, b.rot);
  const board = { x: plaza.x + 1, y: plaza.y + 1 };
  const dropBox = { x: dropAnchor.x, y: dropAnchor.y + 1 };
  const plotBlock = new Uint8Array(W * H);
  for (const p of plotList) for (const i of rectTiles(p.rect)) plotBlock[i] = 1;
  const pathMap = { w: W, h: H, tiles: encode(ground, tier, rampDir) };
  const pathNav = buildNavGrid(pathMap, objects);
  for (const p of plotList) for (const i of rectTiles(p.house)) pathNav.walk[i] = 0;
  for (const door of [shopDoor, museumDoor]) {
    const route = bfsPath(pathNav, pathMap, spawn, door, (i) => !plotBlock[i]);
    if (!route) throw new Retry('no path to a door');
    for (const t of route) { const i = idx(t.x, t.y); if (ground[i] === G.grass && !bridgeMask[i]) ground[i] = G.path; }
  }

  // 9. Trees, rocks and props. A blocking object needs a ring of open, same-tier ground around it,
  //    so no placement can ever cut a walkable region in two.
  const keepOut = new Uint8Array(W * H);
  const mark = (i: number, m: number) => { const x = i % W, y = (i - x) / W; for (let yy = y - m; yy <= y + m; yy++) for (let xx = x - m; xx <= x + m; xx++) if (inB(xx, yy)) keepOut[idx(xx, yy)] = 1; };
  for (let i = 0; i < W * H; i++) if (ground[i] === G.path || ground[i] === G.ramp || bridgeMask[i] || occupied[i]) mark(i, ground[i] === G.ramp ? 2 : 1);
  for (const p of plotList) for (const i of rectTiles(grow(p.rect, 1))) if (inB(i % W, Math.floor(i / W))) keepOut[i] = 1;
  for (const t of [shopDoor, museumDoor, board, dropBox, spawn]) mark(idx(t.x, t.y), 1);
  const openTile = (i: number, t: number) => (ground[i] === G.grass || ground[i] === G.sand) && tier[i] === t && !occupied[i];
  const canBlock = (x: number, y: number, fw: number, fd: number, groundOk: (g: number) => boolean) => {
    const t = tier[idx(x, y)]!;
    for (let yy = y - 1; yy <= y + fd; yy++) for (let xx = x - 1; xx <= x + fw; xx++) {
      if (!inB(xx, yy)) return false;
      const i = idx(xx, yy);
      if (!openTile(i, t)) return false;
      const inner = xx >= x && xx < x + fw && yy >= y && yy < y + fd;
      if (inner && (keepOut[i] || !groundOk(ground[i]!))) return false;
    }
    return true;
  };
  const grassOnly = (g: number) => g === G.grass;
  const nativeFruit: FruitType = rngFrom('fruit', seed).pick(FRUITS);
  // A windmill on the high ground when there is room for one.
  for (const i of rng.shuffle(Array.from({ length: W * H }, (_, k) => k).filter((k) => tier[k]! >= 2))) {
    const x = i % W, y = (i - x) / W;
    if (canBlock(x, y, 3, 3, grassOnly)) { place('w.windmill', x, y, 0, { role: 'windmill' }); break; }
  }
  // Native fruit trees near the plaza and the plots.
  const fruitTarget = rng.int(M.fruitTrees[0], M.fruitTrees[1]);
  const homes = [plaza, ...plotList.map((p) => p.rect)];
  const fruitCands: number[] = [];
  for (let i = 0; i < W * H; i++) {
    const x = i % W, y = (i - x) / W;
    const gap = Math.min(...homes.map((r) => rectGap({ x, y, w: 1, h: 1 }, r)));
    if (gap >= 2 && gap <= 8 && tier[i] === 1) fruitCands.push(i);
  }
  rng.shuffle(fruitCands);
  let fruitTrees = 0;
  for (const i of fruitCands) {
    if (fruitTrees >= fruitTarget) break;
    const x = i % W, y = (i - x) / W;
    if (!canBlock(x, y, 1, 1, grassOnly)) continue;
    place('w.tree_fruit', x, y, 0, { fruit: nativeFruit, plantedAt: null, plantedBy: null });
    fruitTrees++;
  }
  if (fruitTrees < M.fruitTrees[0]) throw new Retry('too few fruit trees');
  // Forest: denser on plateaus and toward the coast, thin around the plaza.
  const treeTarget = rng.int(M.decorTrees[0], M.decorTrees[1]);
  const pcx = pc.x, pcy = pc.y;
  const treeCands: { i: number; s: number }[] = [];
  for (let i = 0; i < W * H; i++) {
    if (ground[i] !== G.grass || keepOut[i]) continue;
    const x = i % W, y = (i - x) / W;
    const ds = distSea[i]!;
    const s = n5(x, y) + (tier[i]! >= 2 ? 0.35 : 0) + (ds >= 2 && ds <= 9 ? 0.2 : 0) - (Math.hypot(x - pcx, y - pcy) < 14 ? 0.5 : 0) + rng.next() * 0.3;
    treeCands.push({ i, s });
  }
  treeCands.sort((a, b) => b.s - a.s);
  let trees = 0;
  for (const { i } of treeCands) {
    if (trees >= treeTarget) break;
    const x = i % W, y = (i - x) / W;
    const big = rng.chance(0.18);
    const high = tier[i]! >= 2;
    const id = big ? (high ? 'w.tree_grove_b' : 'w.tree_grove_a') : rng.pick(high ? ['w.tree_round_b', 'w.tree_grove_b_small'] : ['w.tree_round', 'w.tree_round_b', 'w.tree_grove_a_small']);
    const fp = big ? 2 : 1;
    if (!canBlock(x, y, fp, fp, grassOnly)) continue;
    place(id, x, y, rng.int(0, 3) as Rot, { fruit: null, plantedAt: null, plantedBy: null });
    trees++;
  }
  const scatter = (count: number, ids: readonly string[], groundOk: (g: number) => boolean, blocking: boolean) => {
    let placed = 0;
    for (let tries = 0; tries < count * 40 && placed < count; tries++) {
      const x = rng.int(1, W - 2), y = rng.int(1, H - 2), i = idx(x, y);
      const id = rng.pick(ids), e = worldEntry(id);
      const [fw, fd] = e.footprint;
      if (blocking ? !canBlock(x, y, fw, fd, groundOk) : keepOut[i] || occupied[i] || !groundOk(ground[i]!)) continue;
      place(id, x, y, (fw === fd ? rng.int(0, 3) : 0) as Rot);
      placed++;
    }
  };
  scatter(rng.int(M.rocks[0], M.rocks[1]), ['w.rock_a', 'w.rock_b', 'w.rock_c', 'w.rock_d', 'w.rock_e'], (g) => g === G.grass || g === G.sand, true);
  scatter(24, ['w.bush'], grassOnly, true);
  scatter(8, ['w.log', 'w.lumber'], grassOnly, true);
  scatter(10, ['w.mushrooms'], grassOnly, false);
  scatter(4, ['w.lily'], (g) => g === G.pond, false);

  // 10. Final map + guarantees.
  const tiles = encode(ground, tier, rampDir);
  const water = waterAreas(ground);
  const beach = beachRects(ground, cy);
  const map: MapData = {
    w: W, h: H, genVersion, tiles, plots: plotList, water,
    sites: { plaza, shop, museum, shopDoor, museumDoor, board, dropBox, spawn, beach },
  };
  verify(map, objects);
  return { seed, map, objects, nativeFruit };

  // ----- local helpers that read this attempt's arrays
  function step(i: number, dx: number, dy: number): number { const x = (i % W) + dx, y = Math.floor(i / W) + dy; return inB(x, y) ? idx(x, y) : -1; }
  function hasRiverMouth(): boolean {
    for (let i = 0; i < W * H; i++) {
      if (ground[i] !== G.river) continue;
      const x = i % W, y = (i - x) / W;
      if (DIRS.some(([dx, dy]) => inB(x + dx, y + dy) && ground[idx(x + dx, y + dy)] === G.sea)) return true;
    }
    return false;
  }
  function majority(mask: Uint8Array, passes: number) {
    for (let p = 0; p < passes; p++) {
      const copy = mask.slice();
      for (let y = 1; y < H - 1; y++) for (let x = 1; x < W - 1; x++) {
        let n = 0;
        for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) n += copy[idx(x + dx, y + dy)]!;
        mask[idx(x, y)] = n >= 5 ? 1 : 0;
      }
    }
  }
  function dropSmall(mask: Uint8Array, min: number) {
    const { label, sizes } = components((i) => mask[i] === 1);
    for (let i = 0; i < W * H; i++) if (label[i]! >= 0 && sizes[label[i]!]! < min) mask[i] = 0;
  }
  function bestRect(w: number, h: number, ok: (r: Rect) => boolean, cost: (r: Rect) => number): Rect | null {
    let best: Rect | null = null, bestCost = Infinity;
    for (let y = 1; y + h < H - 1; y++) for (let x = 1; x + w < W - 1; x++) {
      const r = { x, y, w, h };
      if (!ok(r)) continue;
      const c = cost(r);
      if (c < bestCost) { bestCost = c; best = r; }
    }
    return best;
  }
}

function encode(ground: Uint8Array, tier: Uint8Array, rampDir: Uint8Array): Uint8Array {
  const out = new Uint8Array(W * H);
  const names = Object.keys(G) as Ground[];
  for (let i = 0; i < W * H; i++) out[i] = tileByte(names[ground[i]!]!, tier[i] as Tier, rampDir[i] as 0 | 1 | 2 | 3);
  return out;
}
function flood(nav: { w: number; h: number; walk: Uint8Array; tier: Uint8Array }, map: { w: number; h: number; tiles: Uint8Array }, from: TileXY): Uint8Array {
  const seen = new Uint8Array(W * H), q = new Int32Array(W * H);
  let head = 0, tail = 0;
  const s = idx(from.x, from.y);
  if (!nav.walk[s]) return seen;
  seen[s] = 1; q[tail++] = s;
  while (head < tail) {
    const i = q[head++]!, x = i % W, y = (i - x) / W;
    for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
      const nx = x + dx, ny = y + dy;
      if (!inB(nx, ny)) continue;
      const j = idx(nx, ny);
      if (seen[j] || !canStep(nav, map, { x, y }, { x: nx, y: ny })) continue;
      seen[j] = 1; q[tail++] = j;
    }
  }
  return seen;
}
function bfsPath(nav: { w: number; h: number; walk: Uint8Array; tier: Uint8Array }, map: { w: number; h: number; tiles: Uint8Array }, from: TileXY, to: TileXY, allow: (i: number) => boolean): TileXY[] | null {
  const prev = new Int32Array(W * H).fill(-2), q = new Int32Array(W * H);
  let head = 0, tail = 0;
  const s = idx(from.x, from.y), goal = idx(to.x, to.y);
  prev[s] = -1; q[tail++] = s;
  while (head < tail) {
    const i = q[head++]!;
    if (i === goal) { const out: TileXY[] = []; for (let c = i; c !== -1; c = prev[c]!) out.push({ x: c % W, y: Math.floor(c / W) }); return out.reverse(); }
    const x = i % W, y = (i - x) / W;
    for (const [dx, dy] of DIRS) {
      const nx = x + dx, ny = y + dy;
      if (!inB(nx, ny)) continue;
      const j = idx(nx, ny);
      if (prev[j] !== -2 || !allow(j) || !canStep(nav, map, { x, y }, { x: nx, y: ny })) continue;
      prev[j] = i; q[tail++] = j;
    }
  }
  return null;
}
function waterAreas(ground: Uint8Array): WaterArea[] {
  const out: WaterArea[] = [];
  const runsOf = (inSet: (i: number) => boolean) => {
    const runs: [number, number, number][] = [];
    for (let y = 0; y < H; y++) {
      let x = 0;
      while (x < W) {
        if (!inSet(idx(x, y))) { x++; continue; }
        const x0 = x;
        while (x < W && inSet(idx(x, y))) x++;
        runs.push([y, x0, x]);
      }
    }
    return runs;
  };
  for (const kind of ['river', 'pond'] as const) {
    const code = G[kind];
    const { label, sizes } = components((i) => ground[i] === code);
    sizes.forEach((_, n) => out.push({ id: `${kind}${n}`, kind, runs: runsOf((i) => label[i] === n) }));
  }
  const nearLand = distanceFrom((i) => !WATER.has(ground[i]!));
  out.push({ id: 'sea0', kind: 'sea', runs: runsOf((i) => ground[i] === G.sea && nearLand[i]! >= 1 && nearLand[i]! <= 3) });
  return out;
}
function beachRects(ground: Uint8Array, cy: number): Rect[] {
  const out: Rect[] = [];
  const strip = Math.ceil(W / 4);
  for (let s = 0; s < 4; s++) {
    let x0 = Infinity, y0 = Infinity, x1 = -1, y1 = -1, n = 0;
    for (let y = Math.floor(cy); y < H; y++) for (let x = s * strip; x < Math.min(W, (s + 1) * strip); x++) {
      if (ground[idx(x, y)] !== G.sand) continue;
      n++; x0 = Math.min(x0, x); y0 = Math.min(y0, y); x1 = Math.max(x1, x); y1 = Math.max(y1, y);
    }
    if (n >= 10) out.push({ x: x0, y: y0, w: x1 - x0 + 1, h: y1 - y0 + 1 });
  }
  return out;
}
function verify(map: MapData, objects: GenObject[]) {
  const ground = (x: number, y: number) => map.tiles[idx(x, y)]! & 0x0f;
  for (let x = 0; x < W; x++) if (ground(x, 0) !== G.sea || ground(x, H - 1) !== G.sea) throw new Retry('land on the border');
  for (let y = 0; y < H; y++) if (ground(0, y) !== G.sea || ground(W - 1, y) !== G.sea) throw new Retry('land on the border');
  const nav = buildNavGrid(map, objects);
  const reach = flood(nav, map, map.sites.spawn);
  for (let i = 0; i < W * H; i++) if (nav.walk[i] && !reach[i]) throw new Retry('unreachable tile');
  const s = map.sites;
  for (const t of [s.shopDoor, s.museumDoor, s.board, s.dropBox, s.spawn, ...map.plots.map((p) => p.door)]) if (!reach[idx(t.x, t.y)]) throw new Retry('unreachable site');
  for (const k of ['river', 'pond', 'sea'] as const) if (!map.water.some((a) => a.kind === k && a.runs.length > 0)) throw new Retry('missing water kind');
  let land = 0, main = 0;
  for (let i = 0; i < W * H; i++) { const g = map.tiles[i]! & 0x0f; if (g === G.sea) continue; land++; if (((map.tiles[i]! >> 4) & 3) === 1) main++; }
  if (main / land < 0.55) throw new Retry('main level too small');
}
